
import React from 'react';
import { Messages } from '../../../../../gen-src/Localize';
import { DatabaseTableSortOrder, TablesDisplayGridRowDataFragment, TableUpstreamTablesFilteredByColumnQuery, TableUpstreamTablesFilteredByColumnQueryVariables, TableUpstreamTablesQuery, TableUpstreamTablesQueryVariables , TableUpstreamTablesFilteredByColumnDocument , TableUpstreamTablesDocument } from '../../../../../gen-src/queries';
import {
  ConnectionListResult,
  GridSortProps,
  MaxCountsResultData,
  PaginatedQueryWrapper,
} from '../../../data-catalog-shared';
import { TablesDisplayGrid } from '../sharedDisplayGrid/TablesDisplayGrid';
import { TablesValidSortFields } from '../sorting/sortProviders/TablesGridSortProvider';
import { getRowDataWithMaxCountsForTable } from '../table/functions/TableRowDataFunctions';
import { TablesDisplayGridRowData } from '../table/types/TablesDisplayGridRowData';

type QueryVariables = TableUpstreamTablesQueryVariables;
type QueryResults = TableUpstreamTablesQuery;
type QueryResultListNode = TablesDisplayGridRowDataFragment;
type GridRowData = TablesDisplayGridRowData;

type QueryVariablesWithFilter = TableUpstreamTablesFilteredByColumnQueryVariables;
type QueryResultsWithFilter = TableUpstreamTablesFilteredByColumnQuery;

function selectConnectionListResult(resultData: QueryResults): ConnectionListResult<QueryResultListNode> | null {
  const table = resultData && resultData.tables && resultData.tables[0];
  return table.upstreamTablesConnection;
}

function selectConnectionListResultForFilteredQuery(
  resultData: QueryResultsWithFilter
): ConnectionListResult<QueryResultListNode> | null {
  const table = resultData && resultData.tables && resultData.tables[0];
  const column = table && table.columns && table.columns[0];
  return column.upstreamTablesConnection;
}

function selectMaxCountsResult(resultData: QueryResults): MaxCountsResultData {
  const table = resultData && resultData.tables && resultData.tables[0];
  return table;
}

function selectMaxCountsResultForFilteredQuery(resultData: QueryResultsWithFilter): MaxCountsResultData {
  const table = resultData && resultData.tables && resultData.tables[0];
  const column = table && table.columns && table.columns[0];
  return column;
}

function getRowDataForTable(
  table: TablesDisplayGridRowDataFragment,
  resultData: QueryResults,
): TablesDisplayGridRowData {
  return getRowDataWithMaxCountsForTable(table, selectMaxCountsResult(resultData));
}

function getRowDataForTableForFilteredQuery(
  table: TablesDisplayGridRowDataFragment,
  resultData: QueryResultsWithFilter,
): TablesDisplayGridRowData {
  return getRowDataWithMaxCountsForTable(table, selectMaxCountsResultForFilteredQuery(resultData));
}

export type TableUpstreamTablesQueryGridProps =
  GridSortProps<TablesValidSortFields> & {
    readonly tableId: string;
    readonly filteredColumnId: string | null;
    readonly rowCount: number | null;
  };

export function TableUpstreamTablesQueryGrid(props: TableUpstreamTablesQueryGridProps): JSX.Element {
  const emptyGridMessage = Messages.EmptyUpstreamTablesLineageGridMessage();
  const emptyFilteredGridMessage = Messages.EmptyUpstreamFilteredTablesLineageGridMessage();

  return props.filteredColumnId ? (
    <PaginatedQueryWrapper<QueryVariablesWithFilter, QueryResultsWithFilter, QueryResultListNode, GridRowData>
      queryGql={TableUpstreamTablesFilteredByColumnDocument}
      partialQueryVariables={{
        tableId: props.tableId,
        columnId: props.filteredColumnId,
        orderBy: props.orderBy as DatabaseTableSortOrder,
      }}
      selectConnectionListResult={selectConnectionListResultForFilteredQuery}
      mapResultNode={getRowDataForTableForFilteredQuery}
    >
      {({ rowStore }) =>
        <TablesDisplayGrid
          rowStore={rowStore}
          emptyGridMessage={emptyFilteredGridMessage}
          rowCount={props.rowCount}
          orderBy={props.orderBy}
          updateSortOrder={props.updateSortOrder}
        />
      }
    </PaginatedQueryWrapper>
  ) : (
    <PaginatedQueryWrapper<QueryVariables, QueryResults, QueryResultListNode, GridRowData>
      queryGql={TableUpstreamTablesDocument}
      partialQueryVariables={{
        tableId: props.tableId,
        orderBy: props.orderBy as DatabaseTableSortOrder,
      }}
      selectConnectionListResult={selectConnectionListResult}
      mapResultNode={getRowDataForTable}
    >
      {({ rowStore }) =>
        <TablesDisplayGrid
          rowStore={rowStore}
          emptyGridMessage={emptyGridMessage}
          rowCount={props.rowCount}
          orderBy={props.orderBy}
          updateSortOrder={props.updateSortOrder}
        />
      }
    </PaginatedQueryWrapper>
  );
}
